import type { Locale } from "@/i18n/types";

export type BodyArea =
  | "face"
  | "scalp"
  | "eyebrows_lashes"
  | "neck_decollete"
  | "scar_site"
  | "hands"
  | "abdomen"
  | "limb"
  | "full_body"
  | "custom";

export type ProtocolStep = {
  key: string;
  /** Days since protocol start. 0 = same day. */
  dayOffset: number;
  title: Record<Locale, string>;
  checklist: Record<Locale, string[]>;
  selfieExpected: boolean;
  /** Default body area to photograph for this step. */
  bodyArea?: BodyArea;
  /** Optional reference object suggestion (coin, ruler, sticker) for scale. */
  referenceHint?: Record<Locale, string>;
  /** Optional one-liner explaining what this phase is for (overrides the default
   *  derived from dayOffset in `getPhaseGoal`). */
  phaseGoal?: Partial<Record<Locale, string>>;
};

export type ProtocolDef = {
  goal: string;
  /** Billing tier: `core` ships with Premium; `pro` requires the Suivi Pro add-on. */
  tier: "core" | "pro";
  /** If true, render a permanent "consult a specialist" referral banner (e.g. ABCDE). */
  referralRequired?: boolean;
  title: Record<Locale, string>;
  subtitle: Record<Locale, string>;
  /** Vocabulary + red flags injected into AI prompt. */
  aiContext: Record<Locale, string>;
  steps: ProtocolStep[];
  /**
   * Optional opt-in extension phases unlocked once the core phase is finished.
   * Activated via `extendProtocolSession`. Designed to make the most of the
   * monthly subscription on protocols whose biological signal continues to
   * evolve well beyond the core 4-12 weeks (acne cycle, scar maturation,
   * tattoo color settling, sleep habit consolidation, body recomp…).
   */
  extension?: {
    label: Record<Locale, string>;
    rationale: Record<Locale, string>;
    steps: ProtocolStep[];
  };
};

const POST_AESTHETIC: ProtocolDef = {
  goal: "post_aesthetic",
  tier: "core",
  title: {
    fr: "Post-traitement esthétique",
    en: "Post-aesthetic recovery",
    nl: "Post-esthetisch herstel",
  },
  subtitle: {
    fr: "Laser, peeling, microneedling, injections — suivi guidé J+1 à J+30.",
    en: "Laser, peel, microneedling, injectables — guided check-ins from D+1 to D+30.",
    nl: "Laser, peeling, microneedling, injectables — geleide check-ins van D+1 tot D+30.",
  },
  aiContext: {
    fr: "L'utilisateur est en post-traitement esthétique (laser/peeling/microneedling/injections). Repère les red flags : rougeur intense >5j, suintement, douleur croissante, vésicules, fièvre — recommande explicitement une consultation rapide chez le praticien. Conseille hydratation, SPF 50, éviction soleil et gestes agressifs.",
    en: "User is recovering from an aesthetic procedure (laser/peel/microneedling/injectables). Watch for red flags: intense redness >5d, oozing, increasing pain, blistering, fever — explicitly recommend prompt practitioner review. Advise hydration, SPF 50, sun avoidance, no harsh actives.",
    nl: "Gebruiker herstelt na een esthetische behandeling. Let op alarmsignalen (intense roodheid >5d, vocht, toenemende pijn, blaren, koorts) en raad snel praktijkbezoek aan. Adviseer hydratatie, SPF 50, geen agressieve actieve stoffen.",
  },
  steps: [
    { key: "d1", dayOffset: 1, selfieExpected: true,
      bodyArea: "face",
      referenceHint: { fr: "Pièce de 1 € posée près de la zone traitée", en: "1 € coin near the treated area", nl: "Munt van € 1 naast de behandelde zone" },
      title: { fr: "J+1 · Inspection initiale", en: "D+1 · Initial inspection", nl: "D+1 · Eerste inspectie" },
      checklist: {
        fr: [
          "Selfie en lumière naturelle (fenêtre, pas de flash) — pour comparer chaque jour la même chose",
          "Hydratation : +1 L d'eau par rapport à d'habitude — facilite la cicatrisation",
          "Pas d'actifs (rétinol, AHA, BHA) — ces produits exfoliants irritent une peau fraîchement traitée",
          "SPF 50 si vous sortez — les UV peuvent provoquer taches et rebond inflammatoire",
        ],
        en: [
          "Selfie in natural daylight (window, no flash) — to compare apples to apples each day",
          "Hydration: +1 L of water on top of your usual intake — supports healing",
          "No actives (retinol, AHA, BHA) — these exfoliants irritate freshly treated skin",
          "SPF 50 if you go outside — UV can cause dark spots and rebound inflammation",
        ],
        nl: [
          "Selfie in daglicht (raam, geen flits) — om elke dag hetzelfde te kunnen vergelijken",
          "Hydratatie: +1 L water bovenop het gewone — ondersteunt de heling",
          "Geen actieve stoffen (retinol, AHA, BHA) — deze exfoliërende producten irriteren pas behandelde huid",
          "SPF 50 als je naar buiten gaat — UV kan vlekken en herontsteking geven",
        ],
      } },
    { key: "d3", dayOffset: 3, selfieExpected: true,
      bodyArea: "face",
      title: { fr: "J+3 · Évolution rougeur/œdème", en: "D+3 · Redness/edema check", nl: "D+3 · Roodheid/oedeem" },
      phaseGoal: {
        fr: "On vérifie que le pic d'inflammation est passé et que la rougeur recommence à diminuer — pas qu'elle augmente.",
        en: "We check that the inflammation peak is behind you and that redness has started to ease — not climb.",
        nl: "We controleren of de ontstekingspiek voorbij is en de roodheid begint af te nemen — niet toeneemt.",
      },
      checklist: {
        fr: [
          "Selfie (même cadrage qu'à J+1)",
          "Pas de gommage mécanique ou enzymatique — la peau est encore fragile",
          "Crème réparatrice (ex. type cica/panthénol) 2×/jour — apaise et soutient la barrière cutanée",
        ],
        en: [
          "Selfie (same framing as D+1)",
          "No mechanical or enzymatic scrub — skin is still fragile",
          "Repair cream (cica/panthenol type) 2×/day — soothes and supports the skin barrier",
        ],
        nl: [
          "Selfie (zelfde kader als D+1)",
          "Geen mechanische of enzymatische scrub — huid is nog kwetsbaar",
          "Herstelcrème (type cica/panthenol) 2×/d — kalmeert en versterkt de huidbarrière",
        ],
      } },
    { key: "d7", dayOffset: 7, selfieExpected: true,
      bodyArea: "face",
      title: { fr: "J+7 · Première reprise", en: "D+7 · First resume", nl: "D+7 · Eerste hervatting" },
      checklist: { fr: ["Selfie comparatif", "Maquillage léger possible", "SPF 50 maintenu"], en: ["Comparison selfie", "Light makeup ok", "Keep SPF 50"], nl: ["Vergelijkende selfie", "Lichte make-up ok", "SPF 50 behouden"] } },
    { key: "d14", dayOffset: 14, selfieExpected: true,
      bodyArea: "face",
      title: { fr: "J+14 · Bilan mi-parcours", en: "D+14 · Mid-protocol review", nl: "D+14 · Tussentijdse review" },
      checklist: { fr: ["Selfie", "Réintroduction douce des actifs"], en: ["Selfie", "Gentle re-introduction of actives"], nl: ["Selfie", "Voorzichtige herintroductie actieven"] } },
    { key: "d30", dayOffset: 30, selfieExpected: true,
      bodyArea: "face",
      title: { fr: "J+30 · Bilan final", en: "D+30 · Final review", nl: "D+30 · Eindreview" },
      checklist: { fr: ["Selfie", "Rapport PDF pro à partager"], en: ["Selfie", "Pro PDF report to share"], nl: ["Selfie", "Pro PDF-rapport delen"] } },
  ],
  extension: {
    label: { fr: "Suivi long (J+60 → J+90)", en: "Long-tail tracking (D+60 → D+90)", nl: "Lange opvolging (D+60 → D+90)" },
    rationale: {
      fr: "Le remodelage cutané se poursuit 2 à 3 mois après le geste. Prolonger permet d'objectiver les résultats finaux.",
      en: "Skin remodeling continues for 2-3 months after the procedure. Extending captures the true final result.",
      nl: "Huidremodellering gaat 2-3 maanden door. Verlengen toont het echte eindresultaat.",
    },
    steps: [
      { key: "d60", dayOffset: 60, selfieExpected: true, bodyArea: "face",
        title: { fr: "J+60 · Remodelage", en: "D+60 · Remodeling", nl: "D+60 · Remodellering" },
        checklist: { fr: ["Selfie", "Réintroduction actifs progressive"], en: ["Selfie", "Progressive actives re-intro"], nl: ["Selfie", "Geleidelijke herintroductie actieven"] } },
      { key: "d90", dayOffset: 90, selfieExpected: true, bodyArea: "face",
        title: { fr: "J+90 · Résultat consolidé", en: "D+90 · Consolidated result", nl: "D+90 · Geconsolideerd resultaat" },
        checklist: { fr: ["Selfie", "Rapport PDF étendu"], en: ["Selfie", "Extended PDF report"], nl: ["Selfie", "Uitgebreid PDF-rapport"] } },
    ],
  },
};

const PRE_POST_OP: ProtocolDef = {
  goal: "pre_post_op",
  tier: "core",
  title: { fr: "Pré/Post-opératoire", en: "Pre/Post-op tracking", nl: "Pre/Post-operatief" },
  subtitle: {
    fr: "Chirurgie esthétique légère ou dermato — suivi cadré et partageable avec votre chirurgien.",
    en: "Minor aesthetic surgery or derma — structured tracking shareable with your surgeon.",
    nl: "Kleine esthetische ingreep of dermatologie — gestructureerd, deelbaar met je chirurg.",
  },
  aiContext: {
    fr: "Suivi pré/post-opératoire. Pré-op : prépare la peau, sommeil, hydratation, arrêt tabac si possible. Post-op : surveille œdème, hématome, asymétrie ; alerte sur signes infectieux (fièvre, douleur croissante, écoulement purulent). Ne donne aucun avis chirurgical — renvoie systématiquement vers le chirurgien.",
    en: "Pre/post-op tracking. Pre: prep skin, sleep, hydration, smoking cessation if possible. Post: monitor edema, bruising, asymmetry; flag infection signs (fever, increasing pain, purulent discharge). Never give surgical advice — always defer to the surgeon.",
    nl: "Pre/post-operatieve opvolging. Pre: huid voorbereiden, slaap, hydratatie. Post: oedeem/blauwe plekken/asymmetrie volgen; alarm bij koorts, toenemende pijn, etterige uitvloed. Verwijs altijd naar de chirurg.",
  },
  steps: [
    { key: "preop_j-14", dayOffset: -14, selfieExpected: true,
      bodyArea: "custom",
      referenceHint: { fr: "Cadre identique à chaque prise (même distance, même mur)", en: "Identical framing each time (same distance, same wall)", nl: "Identieke kadrering elke keer" },
      title: { fr: "J-14 · Baseline pré-op", en: "D-14 · Pre-op baseline", nl: "D-14 · Pre-op baseline" },
      checklist: { fr: ["Selfie de référence", "Hydratation 2L/j", "Sommeil 7-8h"], en: ["Reference selfie", "2L water/d", "7-8h sleep"], nl: ["Referentieselfie", "2L water/d", "7-8u slaap"] } },
    { key: "postop_j1", dayOffset: 1, selfieExpected: true,
      bodyArea: "scar_site",
      referenceHint: { fr: "Règle ou pièce de 1 € pour échelle", en: "Ruler or 1 € coin for scale", nl: "Liniaal of munt voor schaal" },
      title: { fr: "J+1 · Premier post-op", en: "D+1 · First post-op", nl: "D+1 · Eerste post-op" },
      checklist: { fr: ["Selfie selon consignes chirurgien", "Pas d'effort"], en: ["Selfie per surgeon guidance", "No exertion"], nl: ["Selfie volgens chirurg", "Geen inspanning"] } },
    { key: "postop_j7", dayOffset: 7, selfieExpected: true,
      bodyArea: "scar_site",
      title: { fr: "J+7 · Évolution œdème", en: "D+7 · Edema check", nl: "D+7 · Oedeem-check" },
      checklist: { fr: ["Selfie", "Note douleur (0-10)"], en: ["Selfie", "Pain score (0-10)"], nl: ["Selfie", "Pijnscore (0-10)"] } },
    { key: "postop_j30", dayOffset: 30, selfieExpected: true,
      bodyArea: "scar_site",
      title: { fr: "J+30 · Bilan visite contrôle", en: "D+30 · Follow-up visit", nl: "D+30 · Controlebezoek" },
      checklist: { fr: ["Selfie", "Rapport PDF pour le chirurgien"], en: ["Selfie", "PDF report for surgeon"], nl: ["Selfie", "PDF-rapport voor chirurg"] } },
  ],
};

const MENOPAUSE: ProtocolDef = {
  goal: "meno_andro",
  tier: "core",
  title: { fr: "Ménopause / Andropause", en: "Menopause / Andropause", nl: "Menopauze / Andropauze" },
  subtitle: {
    fr: "Peau, énergie, sommeil, masse — suivi mensuel sur 12 semaines.",
    en: "Skin, energy, sleep, body composition — monthly check-ins over 12 weeks.",
    nl: "Huid, energie, slaap, lichaamssamenstelling — maandelijks over 12 weken.",
  },
  aiContext: {
    fr: "Contexte hormonal (ménopause/andropause). Privilégie habitudes durables : sommeil régulier, force/résistance, apport protéique 1,2-1,6 g/kg/j, oméga-3, vitamine D, gestion du stress. Aucune prescription hormonale ; oriente vers endocrinologue/gynécologue/urologue pour traitement.",
    en: "Hormonal context (menopause/andropause). Favor durable habits: regular sleep, resistance training, protein 1.2-1.6 g/kg/d, omega-3, vitamin D, stress management. No hormonal prescription; refer to endocrinologist/gynaecologist/urologist for treatment.",
    nl: "Hormonale context. Duurzame gewoonten: regelmatige slaap, krachttraining, eiwit 1,2-1,6 g/kg/d, omega-3, vitamine D. Geen hormonale voorschriften; verwijs door naar specialist.",
  },
  steps: [
    { key: "w0", dayOffset: 0, selfieExpected: true,
      bodyArea: "face",
      phaseGoal: {
        fr: "Photo de référence à conserver : visage à ~30 cm, sans maquillage, lumière constante (idéalement même heure chaque session) — tout l'intérêt est de pouvoir comparer 4, 8 et 12 semaines plus tard.",
        en: "Reference photo to keep: face at ~30 cm, no makeup, consistent light (ideally same time of day) — the whole point is being able to compare at 4, 8 and 12 weeks.",
        nl: "Referentiefoto om te bewaren: gezicht op ~30 cm, geen make-up, consistent licht (best telkens zelfde uur) — om later te vergelijken op 4, 8 en 12 weken.",
      },
      referenceHint: {
        fr: "Note l'heure de la photo : reprends la même à chaque session.",
        en: "Note the time of the photo: use the same time at every session.",
        nl: "Noteer het uur van de foto: gebruik telkens hetzelfde uur.",
      },
      title: { fr: "S0 · Baseline", en: "W0 · Baseline", nl: "W0 · Baseline" },
      checklist: { fr: ["Selfie", "Échelle sommeil (1-5)", "Échelle énergie (1-5)"], en: ["Selfie", "Sleep score (1-5)", "Energy score (1-5)"], nl: ["Selfie", "Slaap (1-5)", "Energie (1-5)"] } },
    { key: "w4", dayOffset: 28, selfieExpected: true,
      bodyArea: "face",
      title: { fr: "S4 · Premier bilan", en: "W4 · First review", nl: "W4 · Eerste review" },
      checklist: { fr: ["Selfie", "Force x2/sem cochée"], en: ["Selfie", "2× resistance sessions/wk"], nl: ["Selfie", "2× krachttraining/wk"] } },
    { key: "w8", dayOffset: 56, selfieExpected: true,
      bodyArea: "face",
      title: { fr: "S8 · Mi-parcours", en: "W8 · Mid review", nl: "W8 · Tussentijds" },
      checklist: { fr: ["Selfie", "Apport protéines OK"], en: ["Selfie", "Protein target met"], nl: ["Selfie", "Eiwit-target gehaald"] } },
    { key: "w12", dayOffset: 84, selfieExpected: true,
      bodyArea: "face",
      title: { fr: "S12 · Bilan final", en: "W12 · Final review", nl: "W12 · Eindreview" },
      checklist: { fr: ["Selfie", "Rapport PDF synthèse"], en: ["Selfie", "PDF summary report"], nl: ["Selfie", "PDF-samenvatting"] } },
  ],
};

const ONCO: ProtocolDef = {
  goal: "onco_recovery",
  tier: "core",
  title: { fr: "Récupération oncologique", en: "Oncology recovery", nl: "Oncologisch herstel" },
  subtitle: {
    fr: "Peau, cheveux, sourcils, vitalité — accompagnement bienveillant post-traitement.",
    en: "Skin, hair, brows, vitality — gentle support after treatment.",
    nl: "Huid, haar, wenkbrauwen, vitaliteit — zachte ondersteuning na behandeling.",
  },
  aiContext: {
    fr: "Récupération post-traitement oncologique. Ton TRÈS bienveillant et non culpabilisant. Pas d'allégation curative, pas de promesse de repousse. Conseils doux : hygiène, hydratation, produits sans parfum, repos, micro-mouvements. Renvoie systématiquement à l'équipe soignante avant toute supplémentation (interactions potentielles).",
    en: "Post-oncology recovery. Use a VERY caring, non-blaming tone. No curative claims, no regrowth promises. Gentle advice: hygiene, hydration, fragrance-free products, rest, micro-movements. Always defer to the care team before any supplementation (potential interactions).",
    nl: "Oncologisch herstel. Zeer zorgzame toon, geen schuld. Geen curatieve claims, geen groeibeloften. Zachte adviezen: hygiëne, hydratatie, parfumvrije producten, rust, micro-beweging. Verwijs altijd naar het zorgteam vóór supplementen.",
  },
  steps: [
    { key: "start", dayOffset: 0, selfieExpected: true,
      bodyArea: "face",
      title: { fr: "Démarrage · Point de départ", en: "Start · Baseline", nl: "Start · Baseline" },
      checklist: { fr: ["Selfie", "Validation équipe soignante"], en: ["Selfie", "Care-team OK"], nl: ["Selfie", "OK van zorgteam"] } },
    { key: "m1", dayOffset: 30, selfieExpected: true,
      bodyArea: "scalp",
      referenceHint: { fr: "Vue plongeante du cuir chevelu, même angle à chaque fois", en: "Top-down scalp view, same angle every time", nl: "Bovenaanzicht hoofdhuid, telkens zelfde hoek" },
      title: { fr: "M+1 · Premier mois", en: "M+1 · First month", nl: "M+1 · Eerste maand" },
      checklist: { fr: ["Selfie", "Repos respecté"], en: ["Selfie", "Rest respected"], nl: ["Selfie", "Rust gerespecteerd"] } },
    { key: "m3", dayOffset: 90, selfieExpected: true,
      bodyArea: "eyebrows_lashes",
      title: { fr: "M+3 · Trimestre", en: "M+3 · Quarterly", nl: "M+3 · Kwartaal" },
      checklist: { fr: ["Selfie", "Bilan avec l'équipe"], en: ["Selfie", "Review with care team"], nl: ["Selfie", "Review met zorgteam"] } },
    { key: "m6", dayOffset: 180, selfieExpected: true,
      bodyArea: "face",
      title: { fr: "M+6 · Semestre", en: "M+6 · Half-year", nl: "M+6 · Halfjaar" },
      checklist: { fr: ["Selfie", "Rapport PDF à partager"], en: ["Selfie", "Shareable PDF report"], nl: ["Selfie", "Deelbaar PDF-rapport"] } },
  ],
};

import { EXTRA_PROTOCOLS } from "./definitions-extra";
import { BASE_PROTOCOLS } from "./definitions-base";
export const PROTOCOLS: ProtocolDef[] = [
  ...BASE_PROTOCOLS,
  POST_AESTHETIC, PRE_POST_OP, MENOPAUSE, ONCO,
  ...EXTRA_PROTOCOLS,
];

export function getProtocol(goal: string): ProtocolDef | undefined {
  return PROTOCOLS.find((p) => p.goal === goal);
}

/** Goals that belong to the paid "Suivi Pro" add-on. */
export const PRO_PROTOCOL_GOALS: string[] = PROTOCOLS.filter((p) => p.tier === "pro").map((p) => p.goal);
/** Goals included with the standard Premium plan. */
export const CORE_PROTOCOL_GOALS: string[] = PROTOCOLS.filter((p) => p.tier === "core").map((p) => p.goal);
